<template>
  <v-card class="pa-0">

    <!-- Header bar with source badge -->
    <div class="reg-form-header px-4 pt-4 pb-3">
      <div class="d-flex align-center" style="gap:12px;">
        <div class="headline font-weight-bold" style="white-space:nowrap;">
          {{ form && form.id ? "Update" : "Add" }} Registration Form
        </div>
        <div
          v-if="form && form.id"
          class="reg-form-source-badge"
          :class="isOnlineForm ? 'reg-form-source-online' : 'reg-form-source-manual'"
        >
          <v-icon color="white" small class="mr-1">{{ isOnlineForm ? 'mdi-web' : 'mdi-pencil' }}</v-icon>
          {{ isOnlineForm ? 'Online Form' : 'Manually Entered' }}
        </div>
      </div>
    </div>

    <div class="px-4 pt-4 pb-4">

      <!-- Account info section (always read-only) -->
      <div class="reg-form-info-box mb-4">
        <div class="reg-form-info-row">
          <span class="reg-form-info-label">{{ student && student.id ? "Student" : "Account" }}</span>
          <span class="reg-form-info-value font-weight-medium">{{ getName() }}</span>
        </div>
        <div class="reg-form-info-row">
          <span class="reg-form-info-label">Address</span>
          <span class="reg-form-info-value">{{ getAddress() }}</span>
        </div>
        <div v-if="form && form.enteredOn" class="reg-form-info-row">
          <span class="reg-form-info-label">{{ isOnlineForm ? 'Submitted On' : 'Entered On' }}</span>
          <span class="reg-form-info-value">{{ form.enteredOn | time }}</span>
        </div>
        <div v-if="form && form.enteredBy" class="reg-form-info-row">
          <span class="reg-form-info-label">{{ isOnlineForm ? 'Source' : 'Entered By' }}</span>
          <span class="reg-form-info-value" :style="isOnlineForm ? 'color:#1976d2;font-weight:600;' : ''">
            {{ isOnlineForm ? 'ONLINE' : form.enteredBy }}
          </span>
        </div>
      </div>

      <!-- FORM DETAILS: read-only for both types (Type, Name, DOB) -->
      <div class="mb-3">
        <div class="reg-form-section-title mb-2">Form Details</div>
        <div class="reg-form-info-box">
          <div class="reg-form-info-row">
            <span class="reg-form-info-label">Type</span>
            <span class="reg-form-info-value">{{ votingRegForm.type }}</span>
          </div>
          <div class="reg-form-info-row">
            <span class="reg-form-info-label">Name</span>
            <span class="reg-form-info-value font-weight-medium">{{ votingRegForm.firstName }} {{ votingRegForm.lastName }}</span>
          </div>
          <div class="reg-form-info-row">
            <span class="reg-form-info-label">Date of Birth</span>
            <span class="reg-form-info-value">{{ votingRegForm.dob }}</span>
          </div>
        </div>
      </div>

      <!-- EDITABLE fields: Date Submitted + Status (manual only) or read-only (online) -->
      <div class="mb-1">
        <div class="reg-form-section-title mb-2">Status &amp; Submission</div>

        <!-- Online: read-only -->
        <div v-if="isOnlineForm" class="reg-form-info-box">
          <div class="reg-form-info-row">
            <span class="reg-form-info-label">Date Submitted</span>
            <span class="reg-form-info-value">{{ votingRegForm.dateSubmitted }}</span>
          </div>
          <div class="reg-form-info-row">
            <span class="reg-form-info-label">Status</span>
            <span
              class="reg-form-status-chip"
              :style="{ background: statusBg(votingRegForm.status), color: statusColor(votingRegForm.status) }"
            >
              <v-icon x-small :color="statusColor(votingRegForm.status)" class="mr-1">{{ statusIcon(votingRegForm.status) }}</v-icon>
              {{ votingRegForm.status }}
            </span>
          </div>
        </div>

        <!-- Manual: editable Date Submitted + Status -->
        <div v-if="!isOnlineForm">
          <v-menu
            v-model="votingRegForm.datePickerMenu"
            :close-on-content-click="true"
            offset-y
            full-width
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                v-model="votingRegForm.dateSubmitted"
                label="Date Submitted"
                prepend-icon="mdi-calendar"
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker v-model="votingRegForm.dateSubmitted"></v-date-picker>
          </v-menu>

          <v-select
            v-if="votingRegForm.status !== 'Matched'"
            v-model="votingRegForm.status"
            label="Status"
            :items="['Pending', 'Completed']"
            :rules="[rules.required]"
            ref="status"
          ></v-select>
        </div>
      </div>

      <!-- Matched to DOE banner (both form types) -->
      <div
        v-if="votingRegForm.status === 'Matched' || votingRegForm.isMatchedToDOE"
        class="reg-form-matched-banner mb-3 mt-4"
      >
        <v-icon color="white" class="mr-3">mdi-check-decagram</v-icon>
        <div>
          <div class="font-weight-bold" style="font-size:15px;">Matched to DOE</div>
          <div v-if="votingRegForm.matchedToDoeID" class="caption mt-1" style="opacity:0.9;">
            DOE ID: {{ votingRegForm.matchedToDoeID }}
          </div>
        </div>
      </div>

      <!-- Note — editable for all -->
      <v-text-field
        v-model="votingRegForm.note"
        label="Note"
        ref="note"
      ></v-text-field>

      <v-card-actions class="px-0 pb-0 mt-2">
        <v-spacer></v-spacer>
        <v-btn color="default" dark @click="close()">Cancel</v-btn>
        <v-btn color="warning" dark @click="deleteRegVorm" v-if="allowDeleting() && !isOnlineForm">Delete</v-btn>
        <v-btn :loading="processing" color="success" @click="save">Save</v-btn>
      </v-card-actions>

    </div>
  </v-card>
</template>

<script>
import moment from "moment";
import { getDomain, sum, toISOLocal } from "../../utils/helpers.js";

export default {
  props: {
    form: Object,
    person: Object,
    student: Object,
    reload: Function,
    close: Function,
  },
  data() {
    return {
      processing: false,

      votingRegForm: {
        id: 0,
        firstName: "",
        lastName: "",
        dateSubmitted: "",
        type: "",
        note: "",
        status: "Pending",
        dob: "",
        matchedToDoeID: null,
        isMatchedToDOE: false,
        datePickerMenu: false,
        datePickerMenu2: false,
      },

      rules: {
        required: (value) => !!value || "Required.",
      },
    };
  },
  async created() {},
  computed: {
    isOnlineForm() {
      return this.form && this.form.enteredBy === "ONLINE";
    },
  },
  watch: {
    form(newValue, oldValue) {
      if (newValue && newValue.id) {
        this.votingRegForm.id = newValue.id;
        this.votingRegForm.firstName = newValue.name
          ? newValue.name.split(" ")[0]
          : "";
        this.votingRegForm.lastName = newValue.name
          ? newValue.name.split(" ")[1]
          : "";
        this.votingRegForm.address = newValue.address;
        this.votingRegForm.type = newValue.type;
        this.votingRegForm.dob = newValue.dob
          ? toISOLocal(new Date(newValue.dob)).substr(0, 10)
          : "";
        this.votingRegForm.status = newValue.status;
        this.votingRegForm.note = newValue.note;
        this.votingRegForm.dateSubmitted = newValue.dateSubmitted
          ? toISOLocal(new Date(newValue.dateSubmitted)).substr(0, 10)
          : null;
        this.votingRegForm.matchedToDoeID = newValue.matchedToDoeID || null;
        this.votingRegForm.isMatchedToDOE = !!newValue.isMatchedToDOE;
      }
    },
    student(newValue, oldValue) {
      if (newValue && newValue.id) {
        this.votingRegForm.firstName = newValue.first;
        this.votingRegForm.lastName = newValue.last;
        this.votingRegForm.dob = toISOLocal(new Date(newValue.dob)).substr(0, 10);
      }
    },
    person(newValue, oldValue) {
      if (newValue && newValue.personID) {
        this.votingRegForm.lastName = newValue.lastEnglish;
      }
    },
  },
  methods: {
    getName() {
      if (this.student && this.student.id) {
        return this.student.first + " " + this.student.last;
      }
      if (this.person && this.person.personID) {
        return this.person.firstHebrew + " " + this.person.lastHebrew;
      }
      return;
    },
    getAddress() {
      if (this.student && this.student.id) {
        return this.student.address1 + " " + this.student.city + " " + this.student.zip;
      }
      if (this.person && this.person.personID) {
        return this.person.address1 + " " + this.person.city + " " + this.person.zip;
      }
      return;
    },
    statusIcon(status) {
      switch (status) {
        case "Matched":   return "mdi-check-decagram";
        case "Completed": return "mdi-check-circle";
        case "Submitted": return "mdi-send";
        case "Pending":   return "mdi-clock";
        default:          return "mdi-help-circle";
      }
    },
    statusColor(status) {
      switch (status) {
        case "Matched":   return "#2e7d32";
        case "Completed": return "#388e3c";
        case "Submitted": return "#1565c0";
        case "Pending":   return "#e65100";
        default:          return "#546e7a";
      }
    },
    statusBg(status) {
      switch (status) {
        case "Matched":   return "#e8f5e9";
        case "Completed": return "#f1f8e9";
        case "Submitted": return "#e3f2fd";
        case "Pending":   return "#fff3e0";
        default:          return "#eceff1";
      }
    },
    allowDeleting() {
      return this.form && this.form.id;
    },
    async deleteRegVorm() {
      if (this.isOnlineForm) {
        return;
      }

      try {
        this.processing = true;
        await this.$http.post("api/votingRegstrationFormStatus/delete", [this.form.id]);
        this.$notify({ text: "Registration Form Deleted", group: "notification", type: "success" });
        this.processing = false;
        this.close();
        this.reload();
      } catch (e) {
        this.processing = false;
      }
    },
    resetValidation() {
      if (this.$refs.name) this.$refs.name.resetValidation();
      if (this.$refs.type) this.$refs.type.resetValidation();
      if (this.$refs.status) this.$refs.status.resetValidation();
    },
    validate() {
      if (this.isOnlineForm) return true;

      const validations = [
        this.$refs.type ? this.$refs.type.validate(true) : true,
        this.$refs.status ? this.$refs.status.validate(true) : true,
      ];
      if (this.$refs.name) validations.push(this.$refs.name.validate(true));
      return validations.every(Boolean);
    },
    async save() {
      if (!this.validate()) return;

      if (!this.isOnlineForm && !this.votingRegForm.dob) {
        return this.$notify({ group: "notification", text: "Missing Date of Birth", type: "error" });
      }

      try {
        this.processing = true;

        await this.$http.post("api/votingRegstrationFormStatus/save", [
          {
            id: this.votingRegForm.id,
            dateSubmitted: this.votingRegForm.dateSubmitted || null,
            personID: this.person ? this.person.personID : null,
            studentID: this.student ? this.student.id : null,
            type: this.votingRegForm.type,
            name: this.votingRegForm.firstName + " " + this.votingRegForm.lastName,
            address: this.getAddress(),
            dob: this.votingRegForm.dob,
            note: this.votingRegForm.note,
            status: this.votingRegForm.status,
            enteredBy: this.$store.state.authentication.user,
          },
        ]);

        this.close();
        this.reload();

        this.votingRegForm = {
          id: 0, firstName: "", lastName: "", dateSubmitted: "",
          type: "", note: "", status: "Pending", dob: "",
          matchedToDoeID: null, isMatchedToDOE: false,
          datePickerMenu: false, datePickerMenu2: false,
        };

        this.$notify({ group: "notification", text: this.form ? "Changes Saved" : "Form Saved", type: "success" });
        this.processing = false;
      } catch (e) {
        this.processing = false;
      }
    },
  },
};
</script>

<style>
.scroll-container {
  height: 100%;
  overflow-y: scroll;
  backface-visibility: hidden;
}
.reg-form-header {
  border-bottom: 1px solid #e0e0e0;
}
.reg-form-source-badge {
  display: inline-flex;
  align-items: center;
  color: #fff;
  border-radius: 20px;
  padding: 4px 12px 4px 10px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.3px;
  flex: 0 0 auto;
}
.reg-form-source-online  { background-color: #1976d2; }
.reg-form-source-manual  { background-color: #78909c; }
.reg-form-section-title {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  color: #90a4ae;
}
.reg-form-info-box {
  background: #f8f9fa;
  border: 1px solid #e8ecef;
  border-radius: 8px;
  padding: 10px 14px;
}
.reg-form-info-row {
  display: flex;
  align-items: baseline;
  padding: 4px 0;
  border-bottom: 1px solid #eef0f2;
}
.reg-form-info-row:last-child {
  border-bottom: none;
}
.reg-form-info-label {
  min-width: 130px;
  font-size: 12px;
  font-weight: 600;
  color: #90a4ae;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.reg-form-info-value {
  font-size: 14px;
  color: #37474f;
}
.reg-form-status-chip {
  display: inline-flex;
  align-items: center;
  border-radius: 12px;
  padding: 2px 9px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}
.reg-form-matched-banner {
  display: flex;
  align-items: center;
  background: linear-gradient(135deg, #2e7d32, #4caf50);
  color: #fff;
  border-radius: 8px;
  padding: 12px 18px;
}
</style>
